// ─── Durée « n mois / n années » — module PUR ─────────────────────
// Sert au sélecteur [−] n [＋] [Mois↻] : le nombre s'incrémente d'un cran,
// et le bouton d'unité BASCULE mois ↔ année sans toucher au nombre (12 mois
// devient 12 années). C'est ce que Cédric a décrit le 13/08, et c'est le
// comportement le plus prévisible : le bouton ne change QUE l'unité.
//
// `'tout'` est une valeur à part entière, pas un 0 déguisé : « aucune limite »
// n'est pas « zéro mois ». On l'atteint en dépassant le maximum vers le haut,
// et on en ressort par [−].

export const MOIS = 'mois';
export const ANNEE = 'annee';
export const TOUT = 'tout';

export const DUREE_DEFAUT = { n: 1, unite: MOIS };

const entier = (v, defaut = 1) => {
  const n = Math.trunc(Number(v));
  return Number.isFinite(n) ? n : defaut;
};

// Une durée en nombre de MOIS — la seule unité que les calculs manipulent.
// `'tout'` remonte tel quel : au module appelant de décider ce qu'il en fait
// (ne pas filtrer du tout, en général). Le convertir en un grand nombre
// serait un mensonge silencieux le jour où l'historique dépasse ce nombre.
export function enMois(duree) {
  if (duree === TOUT || duree?.unite === TOUT) return TOUT;
  const n = entier(duree?.n, 1);
  return duree?.unite === ANNEE ? n * 12 : n;
}

// Bascule l'unité SANS convertir le nombre : 12 mois → 12 années.
export function basculerUnite(duree) {
  if (duree === TOUT || duree?.unite === TOUT) return duree;
  return { n: entier(duree?.n, 1), unite: duree?.unite === ANNEE ? MOIS : ANNEE };
}

// Un cran. Le plafond s'exprime en MOIS pour rester comparable entre unités.
// Au-delà, deux comportements : passer à `'tout'` si l'écran le permet, sinon
// rester au plafond — jamais déborder en silence.
export function pas(duree, sens, { maxMois = 120, avecTout = false, min = 1 } = {}) {
  if (duree === TOUT || duree?.unite === TOUT) {
    if (sens > 0) return TOUT;                    // déjà au bout
    const n = duree?.unite === TOUT ? entier(duree?.n, 0) : 0;
    return n ? { n, unite: ANNEE } : { n: Math.trunc(maxMois / 12), unite: ANNEE };
  }
  const unite = duree?.unite === ANNEE ? ANNEE : MOIS;
  const suivant = entier(duree?.n, 1) + sens;
  // `min` vaut 1 par défaut — une durée nulle n’a pas de sens. Mais un FILTRE
  // de période accepte 0 : « ce mois-ci et rien d’autre » est une réponse.
  if (suivant < min) return { n: min, unite };
  const enM = unite === ANNEE ? suivant * 12 : suivant;
  if (enM > maxMois) {
    if (avecTout) return TOUT;
    return { n: unite === ANNEE ? Math.trunc(maxMois / 12) : maxMois, unite };
  }
  return { n: suivant, unite };
}

// Normalise ce qui vient du stockage : un nombre nu vaut des mois, et une
// durée en mois multiple de 12 N'EST PAS convertie en années — l'unité est un
// choix d'affichage de l'utilisateur, pas une déduction.
// `min` : le plancher appartient a l'APPELANT, pas a la fonction. Un filtre
// de periode accepte 0 (« ce mois seul ») la ou un horizon exige 1.
export function normaliser(valeur, min = 1) {
  if (valeur === TOUT) return TOUT;
  if (typeof valeur === 'number') return { n: Math.max(min, entier(valeur, min)), unite: MOIS };
  if (valeur?.unite === TOUT) return TOUT;
  if (valeur?.n === undefined || valeur?.n === null) return { ...DUREE_DEFAUT };
  return { n: Math.max(min, entier(valeur.n, min)), unite: valeur.unite === ANNEE ? ANNEE : MOIS };
}
